import React, { useState, useMemo } from "react";

import {
  Button, Checkbox, Divider, Popover,
} from "antd";

import StyledScrollbar from "./StyledScrollbar";

import "./CountriesCheckboxList.css";

export default function CountriesCheckboxList({
  children, values: countries, disabledItems = [], defaultCheckedCountries = [],
  getIsOpen, onClickSave, selectedCountries, setSelectedCountries,
}) {
  const [open, setOpen] = useState(false);

  /**
   * Set status for disabling country
   * @returns {object} Object of countries as key and true/false for disabled status
   */
  const countriesDisabledStatus = useMemo(() => {
    const disabledCountries = disabledItems.map((item) => item.toLowerCase());
    return countries.reduce((obj, country) => {
      obj[country] = disabledCountries.includes(country.toLowerCase()); // true if disabled}
      return obj;
    }, {});
  }, [disabledItems, countries]);

  /**
   * Get countries which are not disabled
   * @returns {Set} List of countries those are not disabled
   */
  const getNonDisabledCountriesList = () => Array.from(new Set([
    ...defaultCheckedCountries,
    ...Object.keys(countriesDisabledStatus).filter((country) => !countriesDisabledStatus[country]),
  ]));

  /**
   * Handle check event for checkbox
   * @param {*} country
   */
  function handleCheck(country) {
    if (!selectedCountries.includes(country)) {
      setSelectedCountries((state) => Array.from(new Set([...state, ...defaultCheckedCountries, country])));
    } else {
      const updatedCountries = [...selectedCountries];
      if (updatedCountries.includes(country)) {
        const idx = updatedCountries.indexOf(country);
        updatedCountries.splice(idx, 1);
        setSelectedCountries(updatedCountries);
      }
    }
  }

  /**
   * Handle select all checkbox event
   * @param {*} e event
   */
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const nonDisabledCountries = getNonDisabledCountriesList();
      setSelectedCountries(Array.from(nonDisabledCountries));
    } else {
      setSelectedCountries([...defaultCheckedCountries]);
    }
  };

  /**
   * Save button click event
   */
  const handleSave = () => {
    if (onClickSave) onClickSave(selectedCountries);
    setOpen(false);
  };

  /**
   * Handle event for open/close list
   * @param {boolean} newOpen is open
   */
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const CountryList = (
    <div className="CountriesCheckboxList__Container">
      <Checkbox
        checked={getNonDisabledCountriesList().length === selectedCountries.length}
        onChange={handleSelectAll}
      >
        Select all
      </Checkbox>
      <Divider className="CountriesCheckboxList__Divider" />
      <StyledScrollbar className="CountriesCheckboxList__Scrollbar">
        <div className="CountriesCheckboxList__List">
          {countries.map((country, i) => (
            <Checkbox
              key={i}
              checked={selectedCountries.includes(country)}
              onChange={() => handleCheck(country)}
              disabled={countriesDisabledStatus[country]}
              className="CountriesCheckboxList__Checkbox"
            >
              {country}
            </Checkbox>
          ))}
        </div>
      </StyledScrollbar>
      <div className="CountriesCheckboxList__Footer">
        <Button
          type="primary"
          size="small"
          onClick={handleSave}
          disabled={selectedCountries.length === 0}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={CountryList}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      afterOpenChange={getIsOpen}
      open={open}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Popover>
  );
}
